// apps/widget/src/lib/theme.ts
//
// The tenant's brand applied as CSS custom properties. Motion tokens come from
// docs/10 §1.1 and are fixed — smoothness is a product property, not something
// a clinic configures.

import { googleFontHref, HEX_COLOR, readableTextOn, WIDGET_FONT_FAMILIES } from '@skinvizo/shared';
import type { WidgetConfig } from './api';

/**
 * Safe to call again with a changed config — the dashboard's live preview does
 * exactly that — so every optional token is removed when unset rather than
 * left over from the previous call.
 */
export function applyTheme(config: WidgetConfig): void {
  const root = document.documentElement;
  const theme = config.theme;
  const primary = theme.primaryColor || '#111111';
  const set = (name: string, value: string | null): void => {
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  };

  root.style.setProperty('--gs-primary', primary);
  root.style.setProperty(
    '--gs-primary-contrast',
    theme.buttonTextColor && HEX_COLOR.test(theme.buttonTextColor) ? theme.buttonTextColor : contrastOn(primary),
  );
  root.style.setProperty('--gs-radius', `${theme.radius}px`);
  // Unset, the stylesheet's default makes the accent the brand colour.
  set('--gs-accent', theme.accentColor && HEX_COLOR.test(theme.accentColor) ? theme.accentColor : null);

  // A faint wash of the brand colour behind everything. Light enough that the
  // body text keeps its contrast; the plain default sets nothing at all.
  const tinted = theme.backgroundStyle === 'tinted';
  set('--gs-surface', tinted ? 'color-mix(in srgb, var(--gs-primary) 6%, #fff)' : null);
  set('--gs-wash', tinted ? 'color-mix(in srgb, var(--gs-primary) 12%, #fff)' : null);
  set('--gs-line', tinted ? 'color-mix(in srgb, var(--gs-primary) 14%, #e5e7eb)' : null);

  const listed = loadFont(theme.font);
  // Listed names are quoted ("DM Sans" has a space); anything older is used
  // verbatim, as it always was.
  set('--gs-font', theme.font ? `${listed ? `"${theme.font}"` : theme.font}, system-ui, sans-serif` : null);

  // Exits are faster than entrances — a slow exit reads as lag.
  root.style.setProperty('--m-instant', '90ms');
  root.style.setProperty('--m-fast', '140ms');
  root.style.setProperty('--m-base', '200ms');
  root.style.setProperty('--m-panel', '240ms');
  root.style.setProperty('--m-modal', '300ms');
  root.style.setProperty('--m-ease', 'cubic-bezier(.2,0,0,1)');
  root.style.setProperty('--m-ease-exit', 'cubic-bezier(.4,0,1,1)');
}

/**
 * Black or white text, whichever the brand colour can carry.
 *
 * A clinic picking a pale yellow brand colour would otherwise get white text on
 * it, and the CTA — the most important element in the report — becomes
 * unreadable. The rule lives in the shared package so the dashboard's colour
 * picker predicts exactly this choice.
 */
export function contrastOn(hex: string): string {
  return readableTextOn(hex);
}

/**
 * Only fonts from the shared list are fetched, and only when chosen: a clinic
 * on the system font costs its visitors no extra request. A font saved before
 * the list existed is still applied by name, as it always was.
 */
function loadFont(family: string | null): boolean {
  const id = 'gs-font';
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (!family || !(WIDGET_FONT_FAMILIES as readonly string[]).includes(family)) {
    existing?.remove();
    return false;
  }
  const href = googleFontHref(family);
  if (existing?.href === href) return true;
  const link = existing ?? document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  if (!existing) document.head.appendChild(link);
  return true;
}

/** Band colours. Icon + label carry the meaning too — never colour alone. */
export const BAND_COLORS = {
  good: '#16a34a',
  average: '#f59e0b',
  fair: '#dc2626',
} as const;

export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
